import React, { useEffect, useMemo, useState } from 'react';
import { Building2, Download, Star, Users } from 'lucide-react';
import { useExternalUsers } from '@/features/external-users/hooks/useExternalUsers';
import { externalUsersApi } from '@/features/external-users/api/externalUsersApi';
import { ROLE_TYPE_LABELS } from '@/features/external-users/types/external-user.types';
import { useAuthStore } from '@/Stores/auth-v2-store';
import { DataList } from '@/Components/shared/List';
import { useListState, useListSearch } from '@/hooks/list';
import type { ColumnDef, BaseListFilters } from '@/types/list.types';
import { FormSelect } from '@/Components/shared/FormSelect';
import { ExternalUsersMobileCards } from './ExternalUsersMobileCards';

interface ExternalUserFilters extends BaseListFilters {
  roleType?: string;
  status?: string;
}

interface ExternalUserListItem {
  id: string;
  firstname: string;
  lastname: string;
  email: string;
  phone?: string;
  roleType: string;
  societies?: Array<{
    societyId: string;
    societyName: string;
    isPrimary: boolean;
  }>;
  isActive: boolean;
  createdAt?: Date | string;
  lastLoginAt?: Date | string;
}

export interface ExternalUsersListPageProps {
  /** Callback when a row is clicked. If undefined, rows are not clickable. */
  onRowClick?: (user: ExternalUserListItem) => void;
  /** Custom header actions (buttons). If undefined, no header actions. */
  headerActions?: React.ReactNode;
  /** Page title. Default: "Utilisateurs externes" */
  title?: string;
  /** Callback for toggling user status. If undefined, status is displayed as a simple badge. */
  onToggleStatus?: (user: ExternalUserListItem) => void;
  /** Whether the current user can toggle status. Default: true */
  canToggleStatus?: boolean;
  /** Embedded mode (no outer padding/margins). Default: false */
  embedded?: boolean;
  /** Masquer le titre dans le DataList (le parent gère son propre header) */
  hideTitle?: boolean;
  /** Callback pour remonter le total au parent */
  onTotalChange?: (total: number) => void;
  /** Filtrer par société (pour les onglets de la vue société, prioritaire sur le filtre interne) */
  societyId?: string;
}

/**
 * Composant partagé pour la liste des utilisateurs externes
 * Utilisé par admin et outil-pro avec des props différents
 */
export const ExternalUsersListPage: React.FC<ExternalUsersListPageProps> = ({
  onRowClick,
  headerActions,
  title = 'Utilisateurs externes',
  onToggleStatus,
  canToggleStatus = true,
  embedded = false,
  hideTitle = false,
  onTotalChange,
  societyId: fixedSocietyId,
}) => {
  const { currentUser, hasPermission } = useAuthStore();
  // Export réservé à l'admin interne (droit `external_user.export`).
  const canExport = currentUser?.type === 'INTERNAL' && hasPermission('external_user.export');
  const [isExporting, setIsExporting] = useState(false);

  // Gestion de l'état de la liste
  const {
    filters,
    updateFilter,
    pagination,
    setPagination,
    resetFilters,
    activeFiltersCount,
    sortConfig,
    setSortConfig,
  } = useListState<ExternalUserFilters>({
    defaultLimit: 20,
    defaultSortBy: 'lastLoginAt',
    defaultSortDirection: 'DESC',
    persistInUrl: true,
  });

  // Recherche avec debounce 300ms
  const { searchTerm, debouncedSearch, setSearchTerm } = useListSearch({
    debounceMs: 300,
    persistInUrl: true,
  });

  // Société effective : prop fixedSocietyId (onglet société) ou société de l'utilisateur EXTERNAL
  const effectiveSocietyId = fixedSocietyId || (currentUser?.type === 'EXTERNAL' ? currentUser.societyId : undefined);

  // Construction des filtres pour la requête API
  const apiFilters = useMemo(() => ({
    societyId: effectiveSocietyId,
    page: pagination.page,
    limit: pagination.limit,
    sortBy: sortConfig.sortBy,
    sortDirection: sortConfig.sortDirection,
    search: debouncedSearch || undefined,
    roleType: filters.roleType,
    status: filters.status,
  }), [effectiveSocietyId, pagination, sortConfig, debouncedSearch, filters.roleType, filters.status]);

  const { data, isLoading, error } = useExternalUsers(apiFilters);

  const users = useMemo(() => data?.users || [], [data]);
  const total = useMemo(() => data?.total || 0, [data]);

  useEffect(() => { onTotalChange?.(total); }, [total, onTotalChange]);

  // Export XLSX — respecte les filtres affichés (société, recherche, rôle, statut).
  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const blob = await externalUsersApi.exportAll({
        societyId: effectiveSocietyId,
        search: debouncedSearch || undefined,
        roleType: filters.roleType,
        status: filters.status,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `export_utilisateurs_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } finally {
      setIsExporting(false);
    }
  };

  const exportButton = canExport ? (
    <button
      onClick={handleExport}
      disabled={isExporting}
      className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-border tw-border-gray-300 tw-text-gray-700 tw-rounded-lg hover:tw-bg-gray-50 tw-transition-colors tw-font-medium disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
    >
      <Download className="tw-w-4 tw-h-4" />
      {isExporting ? 'Export…' : 'Exporter'}
    </button>
  ) : null;

  const mergedHeaderActions = (exportButton || headerActions) ? (
    <div className="tw-flex tw-items-center tw-gap-2">
      {exportButton}
      {headerActions}
    </div>
  ) : undefined;

  // Afficher la colonne Société pour tous (la recherche filtre par nom de société)
  const hasMultipleSocieties = true;

  // Définition des colonnes
  const columns: ColumnDef<ExternalUserListItem>[] = [
    {
      key: 'societies',
      label: 'Société',
      render: (user) =>
        user.societies && user.societies.length > 0 ? (
          <div className="tw-flex tw-items-center tw-gap-2">
            {(() => {
              const primarySociety = user.societies.find((s) => s.isPrimary);
              const additionalCount = user.societies.length - 1;

              return (
                <>
                  {primarySociety && (
                    <span
                      className={`tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-rounded ${
                        fixedSocietyId && primarySociety.societyId !== fixedSocietyId
                          ? 'tw-bg-blue-100 tw-text-blue-800'
                          : 'tw-bg-yellow-100 tw-text-yellow-800'
                      }`}
                      title={primarySociety.societyName}
                    >
                      <Building2 className="tw-w-3 tw-h-3" />
                      {primarySociety.societyName.length > 20
                        ? `${primarySociety.societyName.substring(0, 20)}...`
                        : primarySociety.societyName}
                      <Star className="tw-w-3 tw-h-3" />
                    </span>
                  )}
                  {additionalCount > 0 && (
                    <span
                      className="tw-inline-flex tw-items-center tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-bg-gray-100 tw-text-gray-700 tw-rounded"
                      title={user.societies
                        .filter((s) => !s.isPrimary)
                        .map((s) => s.societyName)
                        .join(', ')}
                    >
                      + {additionalCount} {additionalCount === 1 ? 'autre' : 'autres'}
                    </span>
                  )}
                </>
              );
            })()}
          </div>
        ) : (
          <span className="tw-text-gray-400">Aucune</span>
        ),
    },
    {
      key: 'name',
      sortKey: 'lastname',
      label: 'Nom',
      render: (user) => (
        <span className="tw-font-medium">
          {user.firstname} {user.lastname}
        </span>
      ),
    },
    {
      key: 'email',
      label: 'Email',
      render: (user) => <span className="tw-text-gray-500">{user.email}</span>,
    },
    {
      key: 'phone',
      label: 'Téléphone',
      render: (user) => <span className="tw-text-gray-500">{user.phone || '-'}</span>,
    },
    {
      key: 'roleType',
      label: 'Rôle',
      render: (user) => (
        <span className="tw-text-gray-500">
          {ROLE_TYPE_LABELS[user.roleType] || user.roleType}
        </span>
      ),
    },
    {
      key: 'createdAt',
      label: 'Date de création',
      render: (user) => (
        <span className="tw-text-gray-500">
          {user.createdAt
            ? new Date(user.createdAt).toLocaleDateString('fr-FR', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
              })
            : '-'}
        </span>
      ),
    },
    {
      key: 'lastLoginAt',
      label: 'Dernière connexion',
      render: (user) => (
        <span className="tw-text-gray-500">
          {user.lastLoginAt
            ? new Date(user.lastLoginAt).toLocaleDateString('fr-FR', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
              })
            : '-'}
        </span>
      ),
    },
    {
      key: 'isActive',
      label: 'Statut',
      render: (user) =>
        onToggleStatus && canToggleStatus ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleStatus(user);
            }}
            className={`tw-inline-flex tw-items-center tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-rounded tw-cursor-pointer tw-transition-colors ${
              user.isActive
                ? 'tw-bg-green-100 tw-text-green-800 hover:tw-bg-green-200'
                : 'tw-bg-red-100 tw-text-red-800 hover:tw-bg-red-200'
            }`}
          >
            {user.isActive ? 'Actif' : 'Inactif'}
          </button>
        ) : (
          <span className={`tw-inline-flex tw-items-center tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-rounded ${
            user.isActive
              ? 'tw-bg-green-100 tw-text-green-800'
              : 'tw-bg-red-100 tw-text-red-800'
          }`}>
            {user.isActive ? 'Actif' : 'Inactif'}
          </span>
        ),
    },
  ];

  // Colonnes visibles (filtrées selon le contexte)
  const visibleColumns = useMemo(
    () => hasMultipleSocieties ? columns : columns.filter((col) => col.key !== 'societies'),
    [columns, hasMultipleSocieties]
  );

  return (
    <DataList
      // Data
      embedded={embedded}
      data={users}
      total={total}
      isLoading={isLoading}
      error={error as Error}
      // Header
      title={hideTitle ? undefined : title}
      titleIcon={hideTitle ? undefined : Users}
      showCount={!hideTitle}
      headerActions={mergedHeaderActions}
      // Colonnes
      columns={visibleColumns}
      // Tri
      sortConfig={sortConfig}
      onSortChange={setSortConfig}
      // Filtres
      filterSearch={
        <div className="tw-flex tw-flex-col tw-gap-1 sm:tw-min-w-[320px]">
          <span className="tw-text-xs tw-text-gray-400 tw-font-normal">Recherche</span>
          <input
            type="text"
            placeholder="Rechercher par nom, email ou société..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="tw-w-full tw-px-3 tw-py-1.5 tw-text-sm tw-border tw-border-gray-300 tw-rounded-lg focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-affinicar-primary focus:tw-border-affinicar-primary"
          />
        </div>
      }
      filters={
        <>
          <div className="tw-flex tw-flex-col tw-gap-1">
            <span className="tw-text-xs tw-text-gray-400 tw-font-normal">Rôle</span>
            <FormSelect
              value={filters.roleType || ''}
              onChange={(value) => updateFilter('roleType', value || undefined)}
              options={[
                { value: '', label: 'Tous' },
                { value: 'ADMIN', label: 'Administrateur' },
                { value: 'USER', label: 'Utilisateur' },
              ]}
              size="sm"
            />
          </div>

          <div className="tw-flex tw-flex-col tw-gap-1">
            <span className="tw-text-xs tw-text-gray-400 tw-font-normal">Statut</span>
            <FormSelect
              value={filters.status || ''}
              onChange={(value) => updateFilter('status', value || undefined)}
              options={[
                { value: '', label: 'Tous' },
                { value: 'active', label: 'Actif' },
                { value: 'inactive', label: 'Inactif' },
              ]}
              size="sm"
            />
          </div>
        </>
      }
      activeFiltersCount={activeFiltersCount}
      onResetFilters={resetFilters}
      // Pagination
      pagination={pagination}
      onPaginationChange={setPagination}
      // Actions
      onRowClick={onRowClick}
      getRowKey={(user) => user.id}
      mobileCardView={ExternalUsersMobileCards}
      // Personnalisation
      emptyMessage="Aucun utilisateur externe pour le moment."
      emptyIcon={Users}
    />
  );
};
